export const getTimeAgo = (dateInput) => {
  const date = new Date(dateInput);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

  const divisions = [
    { amount: 60, name: "second" },
    { amount: 60, name: "minute" },
    { amount: 24, name: "hour" },
    { amount: 30, name: "day" },
    { amount: 12, name: "month" },
    { amount: Infinity, name: "year" },
  ];

  let duration = diffInSeconds;
  for (let i = 0; i < divisions.length; i++) {
    if (Math.abs(duration) < divisions[i].amount) {
      return rtf.format(-Math.floor(duration), divisions[i].name);
    }
    duration /= divisions[i].amount;
  }
};

export const normalizeDate = (date) => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};
export const dateFormatter = ({
  string,
  inputFormat = "yyyy-mm-dd",
  outputFormat = "yyyy-mm-dd",
  offset = 0,
  convertTo = "default", // "default", "IST", "UTC", or "local"
}) => {
  if (!string) return "";

  const pad = (num) => String(num).padStart(2, "0");

  // --- 1️⃣ Clean input (remove timestamp if present) ---
  const cleanString = String(string).split("T")[0];

  // --- 2️⃣ Detect separator and parse input date according to format ---
  const separator = inputFormat.includes("-")
    ? "-"
    : inputFormat.includes("/")
      ? "/"
      : inputFormat.includes(".")
        ? "."
        : "-";

  const inputParts = inputFormat.toLowerCase().split(separator);
  const dateParts = cleanString.split(separator);

  if (inputParts.length !== 3 || dateParts.length !== 3) return "";

  const dateObj = {};
  inputParts.forEach((p, i) => {
    if (p === "yyyy") dateObj.yyyy = dateParts[i];
    if (p === "mm") dateObj.mm = dateParts[i];
    if (p === "dd") dateObj.dd = dateParts[i];
  });

  const { yyyy, mm, dd } = dateObj;
  if (!yyyy || !mm || !dd) return "";

  const date = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
  if (isNaN(date)) return "";

  // --- 3️⃣ Apply day offset ---
  date.setDate(date.getDate() + offset);

  // --- 4️⃣ Convert timezone ---
  let convertedDate = new Date(date);
  const convert = convertTo.toLowerCase();

  if (convert === "utc") {
    convertedDate = new Date(date.toUTCString());
  } else if (convert === "ist") {
    convertedDate = new Date(date.getTime() + 5.5 * 60 * 60 * 1000);
  } else if (convert === "local") {
    convertedDate = new Date(date.toLocaleString());
  }

  // --- 5️⃣ Default output format ---
  if (outputFormat.toLowerCase() === "default") {
    return convertedDate.toISOString().split("T")[0];
  }

  const outputSeparator = outputFormat.includes("-")
    ? "-"
    : outputFormat.includes("/")
      ? "/"
      : outputFormat.includes(".")
        ? "."
        : "-";

  const year = convertedDate.getFullYear();
  const month = pad(convertedDate.getMonth() + 1);
  const day = pad(convertedDate.getDate());

  const formatted = outputFormat
    .toLowerCase()
    .replace("yyyy", year)
    .replace("mm", month)
    .replace("dd", day)
    .split(/[-/.]/)
    .join(outputSeparator);

  return formatted;
};

export const convertTimeHoursFormat = ({ time, hours = "24" }) => {
  // hours =  in which format you want output
  if (!time) return "";

  // Split and extract components
  const parts = time.split(":");
  let [h = "00", m = "00", s = "00", period] = parts;

  let hour = parseInt(h, 10);

  if (hours === "12") {
    // Convert 24 → 12-hour
    const newPeriod = hour >= 12 ? "PM" : "AM";
    if (hour === 0) hour = 12;
    else if (hour > 12) hour -= 12;

    return `${hour.toString().padStart(2, "0")}:${m.padStart(2, "0")}:${s.padStart(2, "0")}:${newPeriod}`;
  }

  if (hours === "24") {
    // Convert 12 → 24-hour
    period = period?.toUpperCase();
    if (period === "PM" && hour < 12) hour += 12;
    if (period === "AM" && hour === 12) hour = 0;

    return `${hour.toString().padStart(2, "0")}:${m.padStart(2, "0")}:${s.padStart(2, "0")}`;
  }

  return time; // fallback if invalid input
};

export const extractTimeFromDate = (date, format = "24") => {
  if (!date) {
    return "";
  }
  let value = convertTimeHoursFormat({ time: date?.split("T")[1], hours: format });
  value = value?.replaceAll(".000Z", "");
  value = value?.replaceAll(".00Z", "");
  return value;
};
export const getNowTime = (format = 24) => {
  if (format !== 12 && format !== 24) {
    throw new Error("Format must be 12 or 24");
  }

  const now = new Date();
  let h = now.getHours();
  const m = now.getMinutes();
  const s = now.getSeconds();

  // 24-hour output
  if (format === 24) {
    return `${pad(h)}:${pad(m)}:${pad(s)}`;
  }

  // Convert to 12-hour
  const period = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12; // 0 → 12, 13 → 1

  return `${pad(h)}:${pad(m)}:${pad(s)} ${period}`;
};

function pad(n) {
  return n.toString().padStart(2, "0");
}

export const formatToDateTimeString = (dateValue) => {
  if (!dateValue) return "";
  const value = String(dateValue).trim();
  const isoMatch = value.match(/^(\d{4}-\d{2}-\d{2})[T\s](\d{2}:\d{2}:\d{2})(?:\.\d+)?(?:Z)?$/);
  if (isoMatch) {
    const [, datePart, timePart] = isoMatch;
    return `${datePart} ${timePart}`;
  }
  return value;
};

/**
 * Returns the current date/time as a formatted string (for filenames, labels, etc.).
 * @param {Object} options
 * @param {boolean} [options.includeDate=true] - Include date part
 * @param {boolean} [options.includeTime=true] - Include time part
 * @param {string} [options.dateFormat='yyyy-mm-dd'] - Date format: 'yyyy-mm-dd', 'dd/mm/yyyy', 'mm-dd-yyyy'
 * @param {number} [options.dayOffset=0] - Days to add to today (e.g. -1 for yesterday)
 * @returns {string} Formatted string, e.g. "2025-01-29 14:30:00"
 */
export const getFormattedCurrentDateTime = ({
  includeDate = true,
  includeTime = true,
  dateFormat = "yyyy-mm-dd",
  dayOffset = 0,
} = {}) => {
  const now = new Date();
  now.setDate(now.getDate() + dayOffset);

  let result = "";

  if (includeDate) {
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    const format = String(dateFormat).toLowerCase();

    switch (format) {
      case "yyyy-mm-dd":
        result += `${yyyy}-${mm}-${dd}`;
        break;
      case "dd/mm/yyyy":
        result += `${dd}/${mm}/${yyyy}`;
        break;
      case "mm-dd-yyyy":
        result += `${mm}-${dd}-${yyyy}`;
        break;
      default:
        result += `${yyyy}-${mm}-${dd}`;
    }
  }

  if (includeTime) {
    if (result) result += " ";
    const hh = String(now.getHours()).padStart(2, "0");
    const min = String(now.getMinutes()).padStart(2, "0");
    const ss = String(now.getSeconds()).padStart(2, "0");
    result += `${hh}:${min}:${ss}`;
  }

  return result.trim();
};

/**
 * Parse a display-formatted date string (e.g. "01 Jan '25" or "01 Jan '25, 17:30:00") to ISO-style string.
 * Reverse of IST-style display formatting.
 * @param {string} formattedDate - Date string like "01 Jan '25" or "01 Jan '25, 17:30:00"
 * @returns {string} ISO-style "YYYY-MM-DD" or "YYYY-MM-DDTHH:mm:ss", or original string if not matched
 * @example
 * parseFormattedDisplayDateToIso("01 Jan '25") // "2025-01-01"
 * parseFormattedDisplayDateToIso("01 Jan '25, 17:30:00") // "2025-01-01T17:30:00"
 */
export const parseFormattedDisplayDateToIso = (formattedDate) => {
  if (!formattedDate) return "";

  const match = formattedDate.match(
    /^(\d{2})\s+([A-Za-z]{3,9})\s+'?(\d{2})'?(?:,\s*(\d{2}):(\d{2}):(\d{2}))?$/
  );

  if (!match) return formattedDate;

  const [, day, rawMonth, year, hh = "00", mm = "00", ss = "00"] = match;
  const monthStr = rawMonth.charAt(0).toUpperCase() + rawMonth.slice(1).toLowerCase();

  const months = {
    Jan: "01",
    Feb: "02",
    Mar: "03",
    Apr: "04",
    May: "05",
    Jun: "06",
    Jul: "07",
    Aug: "08",
    Sep: "09",
    Oct: "10",
    Nov: "11",
    Dec: "12",
  };

  const month = months[monthStr];
  if (!month) return formattedDate;

  const fullYear = `20${year}`;
  const timeProvided = match[4] !== undefined;

  return timeProvided
    ? `${fullYear}-${month}-${day}T${hh}:${mm}:${ss}`
    : `${fullYear}-${month}-${day}`;
};

const INVALID_DATE_MESSAGE = "Invalid date format";

/**
 * Format a date string for display (replaces PDate component logic).
 * Output is intentionally identical to the former PDate component for the same inputs:
 * same format tokens (dd, mm, yyyy, HH, MM, SS), same default date, same en-US time.
 *
 * @param {string} dateString - ISO or parseable date string
 * @param {Object} [options]
 * @param {boolean} [options.getDate=true] - Include formatted date
 * @param {boolean} [options.getTime=false] - Include time (en-US 2-digit)
 * @param {string} [options.format] - Date format: dd, mm, yyyy, HH, MM, SS (e.g. "dd-mm-yyyy", "yyyy-mm-dd")
 * @returns {string} Formatted string, or INVALID_DATE_MESSAGE if invalid (same as PDate "Invalid date format")
 */
export const formatPDateDisplay = (dateString, options = {}) => {
  const { getDate = true, getTime = false, format } = options;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return INVALID_DATE_MESSAGE;

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hour = String(date.getHours()).padStart(2, "0");
  const minute = String(date.getMinutes()).padStart(2, "0");
  const second = String(date.getSeconds()).padStart(2, "0");

  const formattedDate = format
    ? format
        .replace("dd", day)
        .replace("mm", month)
        .replace("yyyy", year)
        .replace("HH", hour)
        .replace("MM", minute)
        .replace("SS", second)
    : `${day}-${month}-${year}`;

  const timeOptions = { hour: "2-digit", minute: "2-digit", second: "2-digit" };
  const formattedTime = date.toLocaleString("en-US", timeOptions);

  if (getDate && getTime) return `${formattedDate} , ${formattedTime}`;
  if (getDate) return formattedDate;
  if (getTime) return formattedTime;
  return "";
};
